import React, { useState } from "react";
import { Calendar as CalendarIcon, Send } from "lucide-react";
import { format } from "date-fns";
import { es as esLocale, eu as euLocale } from "date-fns/locale";
import { toast } from "sonner";
import { useLang } from "../LangContext";
import { SERVICE_OPTIONS, TIME_SLOTS } from "../i18n";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";

const inputCls =
  "w-full bg-white border border-[#1e3a5f]/20 rounded-xl px-4 py-3 text-sm text-[#1e3a5f] placeholder:text-[#1e3a5f]/40 focus:outline-none focus:ring-2 focus:ring-[#7a8b50]/50 focus:border-[#7a8b50]/50 transition";

const labelCls = "text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mb-2 block";

export default function BookingSection() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    date: null,
    time: "",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.date || !form.time || !form.address) {
      toast.error(t.common.requiredField);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        date: format(form.date, "yyyy-MM-dd"),
        time: form.time,
        address: form.address,
        notes: form.notes || "",
      });
      toast.success(t.booking.success);
      setForm({ name: "", email: "", phone: "", service: "", date: null, time: "", address: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err, t.booking.error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reserva"
      className="py-24 md:py-32 bg-[#FAFAF5]"
      data-testid="booking-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#7a8b50]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#7a8b50] font-semibold">
                {t.smallLabels.booking}
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1e3a5f] font-medium tracking-tight leading-tight">
              {t.booking.title}
            </h2>
            <p className="mt-5 text-base text-[#1e3a5f]/70 leading-relaxed">
              {t.booking.subtitle}
            </p>

            <div className="mt-10 hidden lg:block">
              <div className="rounded-3xl border border-[#7a8b50]/20 bg-white p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#7a8b50] font-semibold mb-3">
                  {lang === "es" ? "¿Por qué reservar online?" : "Zergatik online erreserbatu?"}
                </div>
                <ul className="space-y-3 text-sm text-[#1e3a5f]/80">
                  <li>· {lang === "es" ? "Confirmación rápida por WhatsApp" : "Berretsi azkar WhatsApp bidez"}</li>
                  <li>· {lang === "es" ? "Sin compromiso" : "Konpromiso gabe"}</li>
                  <li>· {lang === "es" ? "Atención local Munguía" : "Tokiko arreta Mungian"}</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-white rounded-3xl p-7 md:p-10 border border-[#7a8b50]/20 shadow-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{t.booking.name}</label>
                  <input
                    data-testid="booking-name"
                    className={inputCls}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>{t.booking.phone}</label>
                  <input
                    data-testid="booking-phone"
                    className={inputCls}
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>{t.booking.email}</label>
                  <input
                    data-testid="booking-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label className={labelCls}>{t.booking.service}</label>
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger
                      data-testid="booking-service"
                      className="w-full bg-white border-[#1e3a5f]/20 rounded-xl px-4 py-6 text-sm text-[#1e3a5f] focus:ring-2 focus:ring-[#7a8b50]/50"
                    >
                      <SelectValue placeholder={t.booking.pickService} />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`booking-service-opt-${s}`}>
                          {t.serviceKeys[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className={labelCls}>{t.booking.date}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date-trigger"
                        className={`${inputCls} flex items-center justify-between text-left`}
                      >
                        <span className={form.date ? "text-[#1e3a5f]" : "text-[#1e3a5f]/40"}>
                          {form.date
                            ? format(form.date, "PPP", { locale: lang === "eu" ? euLocale : esLocale })
                            : t.booking.pickDate}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-[#7a8b50]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={(d) => set("date", d)}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        locale={lang === "eu" ? euLocale : esLocale}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className={labelCls}>{t.booking.time}</label>
                  <Select value={form.time} onValueChange={(v) => set("time", v)}>
                    <SelectTrigger
                      data-testid="booking-time"
                      className="w-full bg-white border-[#1e3a5f]/20 rounded-xl px-4 py-6 text-sm text-[#1e3a5f]"
                    >
                      <SelectValue placeholder="--:--" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`booking-time-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>{t.booking.address}</label>
                  <input
                    data-testid="booking-address"
                    className={inputCls}
                    value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>{t.booking.notes}</label>
                  <textarea
                    data-testid="booking-notes"
                    rows={3}
                    className={inputCls}
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                  />
                </div>
              </div>

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#1e3a5f] text-[#FAFAF5] font-semibold text-sm tracking-wide hover:bg-[#15293f] transition-all w-full md:w-auto disabled:opacity-60"
              >
                {submitting ? t.booking.sending : t.booking.submit}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
